import React, { FC, useMemo, useState } from "react";
import Popover from "@mui/material/Popover";

/**
 * HubSpot's date range filter: a select-style button ("Detected: Last 7 days")
 * opening HubSpot's popover with quick ranges on the left and a month
 * calendar for a custom range on the right (click the first day, then the
 * last). Values are local "YYYY-MM-DD" strings; "" means open-ended.
 */

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
/** "Oct 12", or "Oct 12, 2025" outside this year. */
const short = (s: string) => {
  const d = parse(s);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }) });
};

interface Preset {
  key: string;
  label: string;
  range: () => [string, string];
}

const PRESETS: Preset[] = [
  { key: "any", label: "Any time", range: () => ["", ""] },
  { key: "today", label: "Today", range: () => [iso(new Date()), iso(new Date())] },
  { key: "yesterday", label: "Yesterday", range: () => [iso(addDays(new Date(), -1)), iso(addDays(new Date(), -1))] },
  { key: "7", label: "Last 7 days", range: () => [iso(addDays(new Date(), -6)), iso(new Date())] },
  { key: "30", label: "Last 30 days", range: () => [iso(addDays(new Date(), -29)), iso(new Date())] },
  {
    key: "month",
    label: "This month",
    range: () => {
      const t = new Date();
      return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
    },
  },
  {
    key: "lastmonth",
    label: "Last month",
    range: () => {
      const t = new Date();
      return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))];
    },
  },
];

const presetOf = (from: string, to: string): Preset | null => PRESETS.find((p) => {
  const [a, b] = p.range();
  return a === from && b === to;
}) || null;

export const rangeLabel = (from: string, to: string): string => {
  const p = presetOf(from, to);
  if (p) return p.label;
  if (from && to) return from === to ? short(from) : `${short(from)} – ${short(to)}`;
  if (from) return `Since ${short(from)}`;
  return `Until ${short(to)}`;
};

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const Calendar: FC<{ from: string; to: string; onPick: (day: string) => void }> = ({ from, to, onPick }) => {
  const [month, setMonth] = useState(() => {
    const d = from ? parse(from) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const today = iso(new Date());
  const days = useMemo(() => {
    const first = addDays(month, -month.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [month]);
  return (
    <div className="hs-cal">
      <div className="hs-cal__head">
        <button type="button" className="hs-cal__nav hs-focusable" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
          <svg viewBox="0 0 32 32" aria-hidden="true"><path d="M20.5 5l-9 11 9 11" /></svg>
        </button>
        <span className="hs-cal__month">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
        <button type="button" className="hs-cal__nav hs-focusable" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
          <svg viewBox="0 0 32 32" aria-hidden="true"><path d="M11.5 5l9 11-9 11" /></svg>
        </button>
      </div>
      <div className="hs-cal__grid" role="grid">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="hs-cal__weekday" aria-hidden="true">{w}</span>
        ))}
        {days.map((d) => {
          const s = iso(d);
          const edge = s === from || s === to;
          const inside = !!from && !!to && s > from && s < to;
          return (
            <button
              key={s}
              type="button"
              className="hs-cal__day hs-focusable"
              data-outside={d.getMonth() !== month.getMonth() ? "true" : undefined}
              data-today={s === today ? "true" : undefined}
              data-selected={edge ? "true" : undefined}
              data-inrange={inside ? "true" : undefined}
              aria-pressed={edge}
              aria-label={d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              onClick={() => onPick(s)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const HsDateRangeSelect: FC<{
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /** Before the range in the button: "Detected". */
  prefix?: string;
  "aria-label"?: string;
}> = ({ from, to, onChange, prefix, "aria-label": ariaLabel }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<[string, string]>([from, to]);
  const open = (el: HTMLElement) => {
    setDraft([from, to]);
    setAnchor(el);
  };
  const choose = (a: string, b: string) => {
    onChange(a, b);
    setAnchor(null);
  };
  // First click starts a range, the second ends it (either order).
  const pick = (day: string) => {
    const [a, b] = draft;
    if (!a || (a && b)) setDraft([day, ""]);
    else setDraft(day < a ? [day, a] : [a, day]);
  };
  const active = presetOf(from, to);
  const label = rangeLabel(from, to);
  return (
    <>
      <button
        type="button"
        className="hs-select hs-focusable hs-daterange__button"
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        aria-label={ariaLabel}
        onClick={(e) => open(e.currentTarget)}
      >
        <span className="hs-select__label">
          {prefix ? <span className="hs-daterange__prefix">{`${prefix}:\u00a0`}</span> : null}
          {label}
        </span>
        <span className="hs-caret hs-caret--lg" aria-hidden="true" />
      </button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "hs-popover hs-mui-popover", elevation: 0, style: { marginTop: 4 } }}
      >
        <div className="hs-daterange">
          <ul className="hs-daterange__presets" role="listbox" aria-label="Quick ranges">
            {PRESETS.map((p) => (
              <li key={p.key} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={active?.key === p.key}
                  className="hs-menu__item hs-daterange__preset"
                  onClick={() => choose(...p.range())}
                >
                  {p.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="hs-daterange__custom">
            <p className="hs-daterange__hint">
              {draft[0] && draft[1] ? rangeLabel(draft[0], draft[1]) : draft[0] ? `${short(draft[0])} – pick the last day` : "Custom range: pick the first day"}
            </p>
            <Calendar from={draft[0]} to={draft[1]} onPick={pick} />
            <div className="hs-daterange__foot">
              <button type="button" className="hs-btn hs-btn--secondary hs-focusable" onClick={() => setAnchor(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="hs-btn hs-btn--primary hs-focusable"
                disabled={!draft[0]}
                onClick={() => choose(draft[0], draft[1] || draft[0])}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};
